import { Box, Collapse } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MOTION } from "../../theme/tokens";
import type { WageBreakdownRow } from "../../utils/models";
import { prefersReducedMotion } from "../../utils/reducedMotion";
import { CompositionLineView } from "./CompositionLineView";
import type { CompositionLine } from "./wageComposition";

type Props = {
	row: WageBreakdownRow;
	lines: CompositionLine[];
	open: boolean;
	/** The region's id, named by the fold toggle's `aria-controls`. */
	regionId: string;
	agencyName: string | null;
	canViewWage: boolean;
	canOpenWageSettings: boolean;
};

/** The folding region under a segment: the lines that built its rate. */
export const SegmentComposition: FC<Props> = ({ row, lines, open, regionId, agencyName, canViewWage, canOpenWageSettings }) => {
	const { t } = useTranslation();
	return (
		<Collapse
			id={regionId}
			in={open}
			timeout={prefersReducedMotion() ? 0 : { enter: MOTION.DURATION.ENTRANCE_MD, exit: MOTION.DURATION.INLINE_EXIT }}
			easing={MOTION.EASE_OUT}
			// Print is a summary: the rule lines fold away whatever the screen shows (the app's first print rule).
			sx={{ "@media print": { display: "none" } }}
			data-testid={`wage-breakdown-composition-${row.id}`}
		>
			<Box component="ul" aria-label={t("wageBreakdown.composition.ariaLabel", { rateType: row.rateType })} sx={{ m: 0, p: 0, pb: 0.5 }}>
				{lines.map((line, index) => (
					<CompositionLineView
						// biome-ignore lint/suspicious/noArrayIndexKey: lines are a fixed, ordered snapshot of one row; nothing reorders them.
						key={index}
						line={line}
						index={index}
						rowId={row.id}
						agencyName={agencyName}
						canViewWage={canViewWage}
						canOpenWageSettings={canOpenWageSettings}
					/>
				))}
			</Box>
		</Collapse>
	);
};
